import React, { useCallback, useEffect, useMemo, useState, Component } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { Checkbox } from '../components/ui/Checkbox';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { CustomerSelect } from '../components/domain/CustomerSelect';
import { LineItemRow } from '../components/domain/LineItemRow';
import { TotalsPanel } from '../components/domain/TotalsPanel';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { calculateSubtotal, calculateTaxAmount, calculateInvoiceTotal, calculateProfitMargin, addDays } from '../utils/calculations';
import { LineItem, SubscriptionStatus } from '../types';
import { ArrowLeftIcon, PlusIcon, SaveIcon, AlertCircleIcon, CreditCardIcon, CalendarIcon, UserIcon, XCircleIcon, FileTextIcon, Trash2Icon, SettingsIcon, ExternalLinkIcon, MailIcon, PhoneIcon, BuildingIcon } from 'lucide-react';
import { SUBSCRIPTION_FILTERS_STORAGE_KEY } from './Subscriptions';
interface LineItemFormData {
  description: string;
  quantity: number;
  price: number;
  jobCost?: number;
  isTaxable: boolean;
}
interface PaymentMethod {
  id: string;
  card_brand: string;
  card_exp: string;
  card_last_digits: string;
}
// API subscription item type
interface ApiSubscriptionItem {
  title: string;
  qty: number;
  price: number;
  amount: number;
  taxable: number;
}
// API subscription response type
interface ApiSubscriptionResponse {
  id: string;
  customer_id: string;
  customer?: {
    name?: string;
    email?: string;
    organization?: string;
    phone?: string;
  };
  items: ApiSubscriptionItem[];
  customer_payment_method_id?: string;
  created: number;
  start_date: number;
  next_invoice_date: number;
  number: string;
  name: string;
  cycle: string;
  cycle_num?: number;
  amount: number;
  tax: number;
  tax_label?: string;
  fee?: number;
  fee_label?: string;
  status: string;
  installments: number | string;
  passed_installments?: number | string;
  cancel_date?: number;
  invoices_count?: number;
  readonly?: number | string;
  extra?: {
    external_link?: string;
  };
}
export function SubscriptionForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const isEditing = !!id;
  const {
    createSubscription,
    updateSubscription,
    getSubscriptionById,
    selectedCompanyId,
    taxRate
  } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Subscription data from API (for viewing)
  const [subscriptionData, setSubscriptionData] = useState<ApiSubscriptionResponse | null>(null);
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerOrganization, setCustomerOrganization] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [subscriptionName, setSubscriptionName] = useState('');
  const [subscriptionNumber, setSubscriptionNumber] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [nextInvoiceDate, setNextInvoiceDate] = useState<Date | null>(null);
  const [createdDate, setCreatedDate] = useState<Date | null>(null);
  const [cancelDate, setCancelDate] = useState<Date | null>(null);
  const [cycle, setCycle] = useState('month');
  const [cycleNum, setCycleNum] = useState<number>(1);
  const [installments, setInstallments] = useState('0'); // 0 = infinite
  const [passedInstallments, setPassedInstallments] = useState<number>(0);
  const [status, setStatus] = useState<string>('active');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [lineItems, setLineItems] = useState<LineItemFormData[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [storedAmount, setStoredAmount] = useState<number>(0);
  const [storedTax, setStoredTax] = useState<number>(0);
  const [storedTaxLabel, setStoredTaxLabel] = useState<string | undefined>(undefined);
  const [storedFee, setStoredFee] = useState<number>(0);
  const [storedFeeLabel, setStoredFeeLabel] = useState<string | undefined>(undefined);
  const [invoicesCount, setInvoicesCount] = useState<number>(0);
  const [isReadonly, setIsReadonly] = useState(false);
  const [externalLink, setExternalLink] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Additional charges
  const [fee, setFee] = useState<number>(0);
  // Extra settings for labels
  const [taxLineLabel, setTaxLineLabel] = useState('');
  const [feeLineLabel, setFeeLineLabel] = useState('Fee');
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  // Warning modal state
  const [warningMessage, setWarningMessage] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  // Cancel subscription modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // Delete subscription modal state (for future subscriptions)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Edit next invoice date state (for live subscriptions)
  const [isEditingNextDate, setIsEditingNextDate] = useState(false);
  const [editNextDateValue, setEditNextDateValue] = useState('');
  const [isSavingNextDate, setIsSavingNextDate] = useState(false);
  const [nextDateError, setNextDateError] = useState<string | null>(null);
  // Get today's date for minimum date restriction
  const today = new Date().toISOString().split('T')[0];
  // Check if start date is in the future (not today)
  const isStartDateFuture = useMemo(() => {
    if (!startDate) return false;
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return start.getTime() > now.getTime();
  }, [startDate]);
  // Sentinel value for "Don't pay the first invoice right now" option
  const SKIP_PAYMENT_ID = '__skip_payment__';
  // When start date becomes future, clear the "skip payment" selection
  useEffect(() => {
    if (isStartDateFuture && selectedPaymentMethodId === SKIP_PAYMENT_ID) {
      // Auto-select first real payment method if available
      if (paymentMethods.length > 0) {
        setSelectedPaymentMethodId(paymentMethods[0].id);
      } else {
        setSelectedPaymentMethodId('');
      }
    }
  }, [isStartDateFuture, selectedPaymentMethodId, paymentMethods]);
  // Check if subscription is editable (status is "future")
  const isFutureSubscription = isEditing && status === 'future';
  // Check if subscription can be cancelled (status is live only - not future)
  const canCancel = isEditing && status === 'live';
  // Check if subscription can be deleted (status is future, or live with zero invoices)
  const canDelete = isEditing && (status === 'future' || status === 'live' && invoicesCount === 0);
  // Check if we should show the "Open Subscription Invoices" button (status is live, expired, or cancelled)
  const showInvoicesButton = isEditing && (status === 'live' || status === 'expired' || status === 'cancelled');
  // Navigate back to subscriptions list with preserved filters
  const handleBackToSubscriptions = () => {
    const savedFilters = sessionStorage.getItem(SUBSCRIPTION_FILTERS_STORAGE_KEY);
    if (savedFilters) {
      navigate(`/subscriptions?${savedFilters}`);
    } else {
      navigate('/subscriptions');
    }
  };
  // Navigate to invoices page with subscription number as search keyword
  const handleOpenSubscriptionInvoices = () => {
    if (subscriptionNumber) {
      navigate(`/invoices?search=${encodeURIComponent(subscriptionNumber)}`);
    } else {
      navigate('/invoices');
    }
  };
  // Load existing subscription data from API
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!isEditing || !id) return;
      setIsLoadingSubscription(true);
      setLoadError(null);
      try {
        console.log('Fetching subscription:', id);
        const response = await callBillingAPI('getSubscription', {
          id
        });
        console.log('Subscription API response:', response);
        setSubscriptionData(response);
        // Populate form fields
        setCustomerId(response.customer_id || '');
        setCustomerName(response.customer?.name || '');
        setCustomerOrganization(response.customer?.organization || '');
        setCustomerEmail(response.customer?.email || '');
        setCustomerPhone(response.customer?.phone || '');
        setSubscriptionName(response.name || '');
        setSubscriptionNumber(response.number || '');
        setStartDate(new Date(response.start_date * 1000).toISOString().split('T')[0]);
        setNextInvoiceDate(new Date(response.next_invoice_date * 1000));
        setCreatedDate(new Date(response.created * 1000));
        setCancelDate(new Date(response.cancel_date * 1000));
        setCycle(response.cycle || 'month');
        setCycleNum(parseInt(String(response.cycle_num)) || 1);
        setInstallments(String(parseInt(String(response.installments)) || 0));
        setPassedInstallments(parseInt(String(response.passed_installments)) || 0);
        setStatus(response.status || 'live');
        setStoredAmount(parseFloat(String(response.amount)) || 0);
        setStoredTax(parseFloat(String(response.tax)) || 0);
        setStoredTaxLabel(response.tax_label || undefined);
        setStoredFee(parseFloat(String(response.fee)) || 0);
        setStoredFeeLabel(response.fee_label || undefined);
        setFee(parseFloat(String(response.fee)) || 0);
        // Set label fields from API response
        if (response.tax_label) {
          setTaxLineLabel(response.tax_label);
        }
        if (response.fee_label) {
          setFeeLineLabel(response.fee_label);
        }
        setInvoicesCount(parseInt(String(response.invoices_count || 0), 10) || 0);
        // Set readonly flag and external link
        setIsReadonly(response.readonly === 1 || response.readonly === '1');
        setExternalLink(response.extra?.external_link || null);
        // Set cancel date if available
        if (response.cancel_date) {
          setCancelDate(new Date(response.cancel_date * 1000));
        }
        if (response.customer_payment_method_id) {
          setSelectedPaymentMethodId(response.customer_payment_method_id);
        }
        // Transform items
        if (response.items && Array.isArray(response.items)) {
          const transformedItems: LineItemFormData[] = response.items.map((item: ApiSubscriptionItem) => ({
            description: item.title,
            quantity: item.qty,
            price: item.price,
            isTaxable: item.taxable === 1
          }));
          setLineItems(transformedItems.length > 0 ? transformedItems : [{
            description: '',
            quantity: 1,
            price: 0,
            isTaxable: true
          }]);
        }
        setTermsAccepted(true); // Assume terms were accepted for existing subscriptions
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load subscription');
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    fetchSubscription();
  }, [isEditing, id]);
  // Fetch payment methods when customer changes
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!customerId) {
        setPaymentMethods([]);
        if (!isEditing) {
          setSelectedPaymentMethodId('');
        }
        return;
      }
      setIsLoadingPaymentMethods(true);
      try {
        console.log('Fetching payment methods for customer:', customerId);
        const response = await callBillingAPI('getCustomerPaymentMethods', {
          customer_id: customerId
        });
        console.log('Payment methods response:', response);
        // Handle response - could be array directly or wrapped in object
        let methods: PaymentMethod[] = [];
        if (Array.isArray(response)) {
          methods = response;
        } else if (response.items && Array.isArray(response.items)) {
          methods = response.items;
        } else if (response.data && Array.isArray(response.data)) {
          methods = response.data;
        }
        setPaymentMethods(methods);
        // Auto-select first payment method if available (only for new subscriptions)
        if (!isEditing && methods.length > 0 && !selectedPaymentMethodId) {
          setSelectedPaymentMethodId(methods[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        setPaymentMethods([]);
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    };
    fetchPaymentMethods();
  }, [customerId, isEditing]);
  // Calculate totals - for future subscriptions, recalculate from line items
  const calculatedLineItems: LineItem[] = useMemo(() => {
    return lineItems.map((item, index) => ({
      id: `temp-${index}`,
      invoice_id: 'new',
      description: item.description,
      quantity: item.quantity,
      price: item.price,
      job_cost: item.jobCost,
      is_taxable: item.isTaxable,
      profit_margin: calculateProfitMargin(item.price, item.jobCost)
    }));
  }, [lineItems]);
  const subtotal = useMemo(() => isEditing && !isFutureSubscription ? (parseFloat(String(storedAmount)) || 0) - (parseFloat(String(storedTax)) || 0) - (parseFloat(String(storedFee)) || 0) : calculateSubtotal(calculatedLineItems), [isEditing, isFutureSubscription, storedAmount, storedTax, storedFee, calculatedLineItems]);
  // Calculate taxable subtotal (only items marked as taxable)
  const taxableSubtotal = useMemo(() => {
    return lineItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0);
  }, [lineItems]);
  const taxAmount = useMemo(() => isEditing && !isFutureSubscription ? parseFloat(String(storedTax)) || 0 : calculateTaxAmount(calculatedLineItems, taxRate), [isEditing, isFutureSubscription, storedTax, calculatedLineItems, taxRate]);
  const total = useMemo(() => isEditing && !isFutureSubscription ? parseFloat(String(storedAmount)) || 0 : calculateInvoiceTotal(subtotal, taxAmount, 0, 0) + (parseFloat(String(fee)) || 0), [isEditing, isFutureSubscription, storedAmount, subtotal, taxAmount, fee]);
  // Get default tax label based on company tax rate
  const getDefaultTaxLabel = useCallback(() => {
    const taxPercent = taxRate * 100;
    if (taxPercent > 0) {
      return `Tax (${taxPercent.toFixed(0)}%)`;
    }
    return 'Tax';
  }, [taxRate]);
  // Initialize default tax label when creating new subscription
  useEffect(() => {
    if (!isEditing && !taxLineLabel) {
      setTaxLineLabel(getDefaultTaxLabel());
    }
  }, [isEditing, taxLineLabel, getDefaultTaxLabel]);
  // Pre-populate customer from navigation state (e.g., from Customer View page)
  useEffect(() => {
    if (!isEditing && location.state) {
      const state = location.state as {
        customerId?: string;
        customerName?: string;
      };
      if (state.customerId) {
        setCustomerId(state.customerId);
      }
      if (state.customerName) {
        setCustomerName(state.customerName);
      }
    }
  }, [isEditing, location.state]);
  // Calculate next billing date based on cycle and cycle_num
  const getCycleDays = (cycleValue: string, cycleNumValue: number): number => {
    const baseDaysMap: Record<string, number> = {
      week: 7,
      month: 30,
      year: 365
    };
    const baseDays = baseDaysMap[cycleValue] || 30;
    return baseDays * cycleNumValue;
  };
  const nextBillingDate = useMemo(() => {
    // For existing subscriptions (editing), use the API value
    if (isEditing && nextInvoiceDate) {
      return nextInvoiceDate;
    }
    // For new subscriptions, calculate based on start date, cycle, and cycle_num
    return addDays(new Date(startDate), getCycleDays(cycle, cycleNum));
  }, [isEditing, nextInvoiceDate, startDate, cycle, cycleNum]);
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index: number, updates: Partial<LineItemFormData>) => {
    setLineItems(lineItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!customerId) {
      newErrors.customer = 'Please select a customer';
    }
    if (!startDate) {
      newErrors.startDate = 'Please select a start date';
    }
    // Only require terms acceptance for new subscriptions
    if (!isEditing && !termsAccepted) {
      newErrors.terms = 'Customer must accept terms';
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = 'Please add at least one line item';
    }
    // Only validate that descriptions are filled out (price can be zero)
    const hasEmptyDescription = lineItems.some(item => !item.description.trim());
    if (hasEmptyDescription) {
      newErrors.lineItems = 'Please fill in all line item descriptions';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      if (isFutureSubscription && id) {
        // Edit existing future subscription via API
        const items = lineItems.map(item => ({
          title: item.description,
          qty: item.quantity,
          price: item.price,
          amount: item.quantity * item.price,
          taxable: item.isTaxable ? 1 : 0
        }));
        // Convert date from yyyy-mm-dd (input format) to mm-dd-yyyy (API format)
        const dateObj = new Date(startDate);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const year = dateObj.getFullYear();
        const formattedStartDate = `${month}-${day}-${year}`;
        const apiParams: Record<string, any> = {
          id,
          customer_id: customerId,
          start_date: formattedStartDate,
          name: subscriptionName.trim() || '',
          items,
          amount: total,
          tax: {
            amount: taxAmount,
            label: taxLineLabel || getDefaultTaxLabel()
          },
          fee: {
            amount: fee,
            label: feeLineLabel || 'Fee'
          },
          cycle,
          cycle_num: cycleNum,
          installments: parseInt(installments)
        };
        // Handle payment method and ignore_auto_pay logic (same as create)
        if (paymentMethods.length === 0) {
          // No payment methods at all → ignore auto pay
          apiParams.ignore_auto_pay = 1;
        } else if (selectedPaymentMethodId === SKIP_PAYMENT_ID) {
          // User chose "Don't pay the first invoice right now"
          apiParams.ignore_auto_pay = 1;
        } else if (selectedPaymentMethodId) {
          // Normal payment method selected
          apiParams.customer_payment_method_id = selectedPaymentMethodId;
        }
        console.log('Editing subscription with params:', apiParams);
        const response = await callBillingAPI('editSubscription', apiParams);
        console.log('Edit subscription response:', response);
        // Check for warning message
        if (response.warning && response.warning.trim()) {
          setWarningMessage(response.warning);
          setShowWarningModal(true);
        } else {
          handleBackToSubscriptions();
        }
      } else if (isEditing && id) {
        // Update existing subscription (non-future - old logic)
        const subscriptionData = {
          company_id: selectedCompanyId,
          customer_id: customerId,
          start_date: new Date(startDate),
          next_billing_date: nextBillingDate,
          cycle_days: getCycleDays(cycle, cycleNum),
          terms_accepted: termsAccepted,
          status
        };
        await updateSubscription(id, subscriptionData);
        handleBackToSubscriptions();
      } else {
        // Create new subscription via API
        const items = lineItems.map(item => ({
          title: item.description,
          qty: item.quantity,
          price: item.price,
          amount: item.quantity * item.price,
          taxable: item.isTaxable ? 1 : 0
        }));
        // Convert date from yyyy-mm-dd (input format) to mm-dd-yyyy (API format)
        const dateObj = new Date(startDate);
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const year = dateObj.getFullYear();
        const formattedStartDate = `${month}-${day}-${year}`;
        const apiParams: Record<string, any> = {
          items,
          company_id: selectedCompanyId,
          customer_id: customerId,
          installments: parseInt(installments),
          start_date: formattedStartDate,
          cycle,
          cycle_num: cycleNum,
          amount: total,
          tax: {
            amount: taxAmount,
            label: taxLineLabel || getDefaultTaxLabel()
          },
          fee: {
            amount: fee,
            label: feeLineLabel || 'Fee'
          }
        };
        // Add optional fields
        if (subscriptionName.trim()) {
          apiParams.name = subscriptionName.trim();
        }
        // Handle payment method and ignore_auto_pay logic
        if (paymentMethods.length === 0) {
          // No payment methods at all → ignore auto pay
          apiParams.ignore_auto_pay = 1;
        } else if (selectedPaymentMethodId === SKIP_PAYMENT_ID) {
          // User chose "Don't pay the first invoice right now"
          apiParams.ignore_auto_pay = 1;
        } else if (selectedPaymentMethodId) {
          // Normal payment method selected
          apiParams.customer_payment_method_id = selectedPaymentMethodId;
        }
        console.log('Creating subscription with params:', apiParams);
        const response = await callBillingAPI('createSubscription', apiParams);
        console.log('Create subscription response:', response);
        // Check for warning message
        if (response.warning && response.warning.trim()) {
          setWarningMessage(response.warning);
          setShowWarningModal(true);
        } else {
          handleBackToSubscriptions();
        }
      }
    } catch (error) {
      console.error('Failed to save subscription:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save subscription'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleWarningClose = () => {
    setShowWarningModal(false);
    setWarningMessage('');
    handleBackToSubscriptions();
  };
  const handleCancelSubscription = async () => {
    if (!id) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      console.log('Cancelling subscription:', id);
      await callBillingAPI('cancelSubscription', {
        id
      });
      console.log('Subscription cancelled successfully');
      setShowCancelModal(false);
      handleBackToSubscriptions();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setCancelError(error instanceof Error ? error.message : 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };
  const handleDeleteSubscription = async () => {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      console.log('Deleting subscription:', id);
      await callBillingAPI('deleteSubscription', {
        id
      });
      console.log('Subscription deleted successfully');
      setShowDeleteModal(false);
      handleBackToSubscriptions();
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete subscription');
    } finally {
      setIsDeleting(false);
    }
  };
  // Handle saving next invoice date
  const handleSaveNextDate = async () => {
    if (!id || !editNextDateValue) return;
    setIsSavingNextDate(true);
    setNextDateError(null);
    try {
      // Convert from yyyy-mm-dd to MM-DD-YYYY
      const dateObj = new Date(editNextDateValue);
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const year = dateObj.getFullYear();
      const formattedDate = `${month}-${day}-${year}`;
      await callBillingAPI('editSubscriptionNextDate', {
        id,
        next_date: formattedDate
      });
      // Update local state
      setNextInvoiceDate(dateObj);
      setIsEditingNextDate(false);
      setEditNextDateValue('');
    } catch (error) {
      console.error('Failed to update next invoice date:', error);
      setNextDateError(error instanceof Error ? error.message : 'Failed to update next invoice date');
    } finally {
      setIsSavingNextDate(false);
    }
  };
  // Get tomorrow's date for minimum next invoice date
  const getTomorrowDate = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const cycleOptions = [{
    value: 'week',
    label: 'Week(s)'
  }, {
    value: 'month',
    label: 'Month(s)'
  }, {
    value: 'year',
    label: 'Year(s)'
  }];
  const installmentOptions = [{
    value: '0',
    label: 'Infinite'
  }, {
    value: '1',
    label: '1'
  }, {
    value: '2',
    label: '2'
  }, {
    value: '3',
    label: '3'
  }, {
    value: '4',
    label: '4'
  }, {
    value: '5',
    label: '5'
  }, {
    value: '6',
    label: '6'
  }, {
    value: '7',
    label: '7'
  }, {
    value: '8',
    label: '8'
  }, {
    value: '9',
    label: '9'
  }, {
    value: '10',
    label: '10'
  }, {
    value: '11',
    label: '11'
  }, {
    value: '12',
    label: '12'
  }];
  // Helper to get card brand display
  const getCardBrandDisplay = (brand: string) => {
    const brandUpper = brand?.toUpperCase() || 'CARD';
    const brandColors: Record<string, string> = {
      VISA: 'bg-blue-600',
      MASTERCARD: 'bg-red-500',
      AMEX: 'bg-blue-400',
      DISCOVER: 'bg-orange-500'
    };
    return {
      name: brandUpper,
      color: brandColors[brandUpper] || 'bg-gray-600'
    };
  };
  // Get status badge
  const getStatusBadge = (statusValue: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
    }> = {
      live: {
        variant: 'success',
        label: 'Live'
      },
      future: {
        variant: 'info',
        label: 'Future'
      },
      expired: {
        variant: 'default',
        label: 'Expired'
      },
      cancelled: {
        variant: 'danger',
        label: 'Cancelled'
      }
    };
    const config = statusConfig[statusValue] || {
      variant: 'default' as const,
      label: statusValue
    };
    return <Badge variant={config.variant} data-id="element-3032">{config.label}</Badge>;
  };
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Format billing cycle display
  const formatBillingCycle = (cycleValue: string, cycleNumValue: number): string => {
    const cycleNames: Record<string, {
      singular: string;
      plural: string;
    }> = {
      week: {
        singular: 'Week',
        plural: 'Weeks'
      },
      month: {
        singular: 'Month',
        plural: 'Months'
      },
      year: {
        singular: 'Year',
        plural: 'Years'
      }
    };
    const cycleName = cycleNames[cycleValue] || {
      singular: cycleValue,
      plural: cycleValue
    };
    if (cycleNumValue === 1) {
      return `Every ${cycleName.singular}`;
    }
    return `Every ${cycleNumValue} ${cycleName.plural}`;
  };
  // Line items table columns for viewing
  const lineItemColumns = [{
    key: 'description',
    header: 'Description',
    render: (item: LineItemFormData) => <span className="font-medium text-gray-900" data-id="element-3033">{item.description}</span>
  }, {
    key: 'quantity',
    header: 'Qty',
    render: (item: LineItemFormData) => item.quantity
  }, {
    key: 'price',
    header: 'Price',
    render: (item: LineItemFormData) => formatCurrency(item.price)
  }, {
    key: 'amount',
    header: 'Amount',
    render: (item: LineItemFormData) => formatCurrency(item.quantity * item.price)
  }, {
    key: 'taxable',
    header: 'Taxable',
    render: (item: LineItemFormData) => <Badge variant={item.isTaxable ? 'success' : 'default'} data-id="element-3034">
          {item.isTaxable ? 'Yes' : 'No'}
        </Badge>
  }];
  // Determine if we should show editable form (new subscription OR future subscription)
  const showEditableForm = !isEditing || isFutureSubscription;
  // Loading state for editing
  if (isEditing && isLoadingSubscription) {
    return <div className="space-y-6" data-id="element-3035">
        <div className="flex items-center gap-4" data-id="element-3036">
          <Button variant="ghost" onClick={handleBackToSubscriptions} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-3038" />} data-id="element-3037">
            Back
          </Button>
        </div>
        <Card data-id="element-3039">
          <div className="p-12 text-center" data-id="element-3040">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-3041"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-3042">
              Loading subscription...
            </p>
          </div>
        </Card>
      </div>;
  }
  // Error state for editing
  if (isEditing && loadError) {
    return <div className="space-y-6" data-id="element-3043">
        <div className="flex items-center gap-4" data-id="element-3044">
          <Button variant="ghost" onClick={handleBackToSubscriptions} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-3046" />} data-id="element-3045">
            Back
          </Button>
        </div>
        <Card data-id="element-3047">
          <div className="p-12 text-center" data-id="element-3048">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-3049">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-3050" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-3051">
              Failed to load subscription
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-3052">{loadError}</p>
            <Button size="sm" onClick={() => window.location.reload()} data-id="element-3053">
              Retry
            </Button>
          </div>
        </Card>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3054">
      {/* Page Header */}
      <div className="flex items-center gap-4" data-id="element-3055">
        <Button variant="ghost" onClick={handleBackToSubscriptions} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-3057" />} data-id="element-3056">
          Back
        </Button>
        <div className="flex-1" data-id="element-3058">
          <div className="flex items-center gap-3" data-id="element-3059">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3060">
              {isEditing ? isFutureSubscription ? `Edit Subscription ${subscriptionNumber}` : subscriptionName || `Subscription ${subscriptionNumber}` : 'New Subscription'}
            </h1>
            {isEditing && getStatusBadge(status)}
          </div>
          {isEditing && subscriptionNumber && <p className="text-gray-500 mt-1" data-id="element-3061">#{subscriptionNumber}</p>}
        </div>
        {isReadonly && externalLink && <a href={externalLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors" data-id="element-3062">
            <ExternalLinkIcon className="w-4 h-4" data-id="element-3063" />
            Open in Zoho
          </a>}
      </div>

      {/* Auto-generate Invoice Notice */}
      {!isEditing && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-3064">
          <AlertCircleIcon className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-3065" />
          <div data-id="element-3066">
            <p className="text-sm font-medium text-blue-900" data-id="element-3067">
              First Invoice Auto-Generated
            </p>
            <p className="text-sm text-blue-700 mt-1" data-id="element-3068">
              When you create this subscription, the first invoice will be
              automatically generated and sent to the customer.
            </p>
          </div>
        </div>}

      {/* Future Subscription Edit Notice */}
      {isFutureSubscription && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-3069">
          <AlertCircleIcon className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-3070" />
          <div data-id="element-3071">
            <p className="text-sm font-medium text-blue-900" data-id="element-3072">
              Editing Future Subscription
            </p>
            <p className="text-sm text-blue-700 mt-1" data-id="element-3073">
              This subscription hasn't started yet. You can modify all details
              before it becomes active.
            </p>
          </div>
        </div>}

      {/* Error message */}
      {errors.submit && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-3074">
          <p className="text-sm text-red-600" data-id="element-3075">{errors.submit}</p>
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3076">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3077">
          {/* Subscription Details */}
          <Card data-id="element-3078">
            <CardHeader title="Subscription Details" data-id="element-3079" />
            {showEditableForm ?
          // Edit mode for new subscription OR future subscription
          <div className="space-y-4" data-id="element-3080">
                <Input label="Subscription Name" value={subscriptionName} onChange={e => setSubscriptionName(e.target.value)} placeholder="e.g., Monthly Service Plan" helperText="Optional" data-id="element-3081" />
                <CustomerSelect value={customerId} onChange={setCustomerId} error={errors.customer} selectedLabel={customerName} data-id="element-3082" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3083">
                  <DatePicker label="Start Date" value={startDate} onChange={e => setStartDate(e.target.value)} error={errors.startDate} min={today} data-id="element-3084" />
                  <div className="grid grid-cols-2 gap-2" data-id="element-3085">
                    <Input label="Every" type="number" min="1" value={cycleNum} onChange={e => setCycleNum(Math.max(1, parseInt(e.target.value) || 1))} data-id="element-3086" />
                    <Select label="Period" options={cycleOptions} value={cycle} onChange={e => setCycle(e.target.value)} data-id="element-3087" />
                  </div>
                  <Select label="Installments" options={installmentOptions} value={installments} onChange={e => setInstallments(e.target.value)} helperText="Number of billing cycles (Infinite = ongoing)" data-id="element-3088" />
                </div>
                {!isEditing && <div className="mt-4" data-id="element-3089">
                    <Checkbox label="Customer has accepted terms and conditions" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} error={errors.terms} data-id="element-3090" />
                  </div>}
              </div> :
          // View mode for existing subscription (non-future)
          <div className="space-y-4" data-id="element-3091">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3092">
                  <div data-id="element-3093">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3094">
                      Subscription Name
                    </label>
                    <p className="text-gray-900" data-id="element-3095">{subscriptionName || '—'}</p>
                  </div>
                  <div className="relative" data-id="element-3096">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3097">
                      Customer
                    </label>
                    <div className="flex items-center gap-2 cursor-pointer group" onClick={() => setShowCustomerPopup(!showCustomerPopup)} data-id="element-3098">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-3099" />
                      <div data-id="element-3100">
                        <p className="text-blue-600 font-medium group-hover:underline" data-id="element-3101">
                          {customerName || 'Unknown'}
                        </p>
                        {customerOrganization && customerOrganization.trim() !== (customerName || '').trim() && <p className="text-xs text-gray-500" data-id="element-3102">
                              {customerOrganization}
                            </p>}
                      </div>
                    </div>
                    {showCustomerPopup && <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowCustomerPopup(false)} data-id="element-3103" />
                        <div className="absolute left-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[240px]" data-id="element-3104">
                          <div className="space-y-2" data-id="element-3105">
                            <p className="font-medium text-gray-900 text-sm" data-id="element-3106">
                              {customerName}
                            </p>
                            {customerOrganization && customerOrganization.trim() !== (customerName || '').trim() && <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3107">
                                  <BuildingIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3108" />
                                  {customerOrganization}
                                </div>}
                            {customerEmail && <div className="flex items-center gap-2 text-sm" data-id="element-3109">
                                <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3110" />
                                <a href={`mailto:${customerEmail}`} className="text-blue-600 hover:underline" data-id="element-3111">
                                  {customerEmail}
                                </a>
                              </div>}
                            {customerPhone && <div className="flex items-center gap-2 text-sm" data-id="element-3112">
                                <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3113" />
                                <a href={`tel:${customerPhone}`} className="text-gray-700 hover:underline" data-id="element-3114">
                                  {customerPhone}
                                </a>
                              </div>}
                          </div>
                        </div>
                      </>}
                  </div>
                  <div data-id="element-3115">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3116">
                      Start Date
                    </label>
                    <div className="flex items-center gap-2" data-id="element-3117">
                      <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-3118" />
                      <p className="text-gray-900" data-id="element-3119">
                        {formatDate(new Date(startDate))}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-3120">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3121">
                      Billing Cycle
                    </label>
                    <p className="text-gray-900" data-id="element-3122">
                      {formatBillingCycle(cycle, cycleNum)}
                    </p>
                  </div>
                  <div data-id="element-3123">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3124">
                      Installments
                    </label>
                    <p className="text-gray-900" data-id="element-3125">
                      {installments === '0' ? 'Infinite' : installments}
                    </p>
                  </div>
                  <div data-id="element-3126">
                    <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3127">
                      Status
                    </label>
                    {getStatusBadge(status)}
                  </div>
                  {createdDate && <div data-id="element-3128">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3129">
                        Created
                      </label>
                      <p className="text-gray-900" data-id="element-3130">{formatDate(createdDate)}</p>
                    </div>}
                  {status === 'cancelled' && cancelDate && <div data-id="element-3131">
                      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-3132">
                        Cancelled
                      </label>
                      <p className="text-red-600 font-medium" data-id="element-3133">
                        {formatDate(cancelDate)}
                      </p>
                    </div>}
                </div>
              </div>}
          </Card>

          {/* Line Items */}
          <Card padding={!showEditableForm ? 'none' : undefined} data-id="element-3134">
            <div className={!showEditableForm ? 'p-6 border-b border-gray-200' : ''} data-id="element-3135">
              <CardHeader title="Recurring Items" subtitle={showEditableForm ? 'These items will be billed each cycle' : 'Items billed each cycle'} action={showEditableForm && <Button variant="secondary" size="sm" onClick={addLineItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3138" />} data-id="element-3137">
                      Add Item
                    </Button>} data-id="element-3136" />
            </div>
            {errors.lineItems && showEditableForm && <p className="text-sm text-red-600 mb-4" data-id="element-3139">{errors.lineItems}</p>}
            {showEditableForm ? <div className="space-y-4" data-id="element-3140">
                {lineItems.map((item, index) => <LineItemRow key={index} index={index} description={item.description} quantity={item.quantity} price={item.price} jobCost={item.jobCost} isTaxable={item.isTaxable} onDescriptionChange={value => updateLineItem(index, {
              description: value
            })} onQuantityChange={value => updateLineItem(index, {
              quantity: value
            })} onPriceChange={value => updateLineItem(index, {
              price: value
            })} onJobCostChange={value => updateLineItem(index, {
              jobCost: value
            })} onTaxableChange={value => updateLineItem(index, {
              isTaxable: value
            })} onRemove={() => removeLineItem(index)} canRemove={lineItems.length > 1} data-id="element-3141" />)}
              </div> : <Table columns={lineItemColumns} data={lineItems} keyExtractor={(_, index) => String(index)} emptyMessage="No items" data-id="element-3142" />}
          </Card>

          {/* Additional Charges (only for editable subscriptions) */}
          {showEditableForm && <Card data-id="element-3143">
              <CardHeader title="Additional Charges" data-id="element-3144" />
              <div className="grid grid-cols-1 gap-4" data-id="element-3145">
                <Input label="Fee" type="number" min="0" step="0.01" value={fee} onChange={e => setFee(parseFloat(e.target.value) || 0)} helperText="Additional fee charged each billing cycle" data-id="element-3146" />
              </div>
            </Card>}

          {/* Extra Settings (only for editable subscriptions) */}
          {showEditableForm && <Card data-id="element-3147">
              <CardHeader title="Extra Settings" data-id="element-3148" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3149">
                <Input label="Tax Line Label" value={taxLineLabel} onChange={e => setTaxLineLabel(e.target.value)} placeholder={getDefaultTaxLabel()} helperText="Label shown for tax in billing summary" data-id="element-3150" />
                <Input label="Fee Line Label" value={feeLineLabel} onChange={e => setFeeLineLabel(e.target.value)} placeholder="Fee" helperText="Label shown for fee in billing summary" data-id="element-3151" />
              </div>
            </Card>}

          {/* Open Subscription Invoices Button (for live, expired, or cancelled subscriptions) */}
          {showInvoicesButton && !isReadonly && <Button className="w-full" variant="primary" onClick={handleOpenSubscriptionInvoices} leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-3153" />} disabled={invoicesCount === 0} data-id="element-3152">
              Open Subscription Invoices ({invoicesCount})
            </Button>}
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-3154">
          {/* Billing Info */}
          <Card data-id="element-3155">
            <CardHeader title="Billing Schedule" data-id="element-3156" />
            <div className="space-y-3 text-sm" data-id="element-3157">
              <div className="flex justify-between" data-id="element-3158">
                <span className="text-gray-500" data-id="element-3159">Start Date</span>
                <span className="font-medium text-gray-900" data-id="element-3160">
                  {new Date(startDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3161">
                <span className="text-gray-500" data-id="element-3162">Billing Cycle</span>
                <span className="font-medium text-gray-900" data-id="element-3163">
                  {formatBillingCycle(cycle, cycleNum)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3164">
                <span className="text-gray-500" data-id="element-3165">Installments</span>
                <span className="font-medium text-gray-900" data-id="element-3166">
                  {installments === '0' ? 'Infinite' : installments}
                </span>
              </div>
              {!isReadonly && status !== 'cancelled' && status !== 'expired' && (isEditingNextDate ? <div className="space-y-2 pt-1" data-id="element-3167">
                    <span className="text-gray-500" data-id="element-3168">Next Invoice</span>
                    <div className="flex gap-2" data-id="element-3169">
                      <div className="flex-1" data-id="element-3170">
                        <DatePicker value={editNextDateValue} onChange={e => {
                    setEditNextDateValue(e.target.value);
                    setNextDateError(null);
                  }} min={getTomorrowDate()} data-id="element-3171" />
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-3172">
                      <Button size="sm" onClick={handleSaveNextDate} isLoading={isSavingNextDate} disabled={!editNextDateValue} className="flex-1" data-id="element-3173">
                        Save
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => {
                  setIsEditingNextDate(false);
                  setEditNextDateValue('');
                  setNextDateError(null);
                }} disabled={isSavingNextDate} className="flex-1" data-id="element-3174">
                        Cancel
                      </Button>
                    </div>
                    {nextDateError && <p className="text-sm text-red-600" data-id="element-3175">{nextDateError}</p>}
                  </div> : <div className="flex justify-between items-center" data-id="element-3176">
                    <span className="text-gray-500" data-id="element-3177">
                      {parseInt(installments) !== 0 && passedInstallments === parseInt(installments) ? 'Expired at' : 'Next Invoice'}
                    </span>
                    <span className="font-medium text-gray-900 flex items-center gap-2" data-id="element-3178">
                      {nextBillingDate.toLocaleDateString()}
                      {status === 'live' && <button type="button" onClick={() => {
                  setEditNextDateValue(nextBillingDate.toISOString().split('T')[0]);
                  setIsEditingNextDate(true);
                  setNextDateError(null);
                }} className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-3179">
                          Edit
                        </button>}
                    </span>
                  </div>)}
            </div>
          </Card>
          {/* Totals */}
          <Card data-id="element-3180">
            <CardHeader title="Per-Cycle Amount" data-id="element-3181" />
            <TotalsPanel subtotal={subtotal} taxAmount={taxAmount} taxRate={isEditing && !isFutureSubscription ? taxableSubtotal > 0 ? storedTax / taxableSubtotal : 0 : taxRate} taxLabel={isEditing && !isFutureSubscription ? storedTaxLabel : taxLineLabel} fee={isEditing && !isFutureSubscription ? storedFee : fee} feeLabel={isEditing && !isFutureSubscription ? storedFeeLabel : feeLineLabel} total={total} data-id="element-3182" />
          </Card>
          {/* Payment Method */}
          {!isReadonly && <Card data-id="element-3183">
              <CardHeader title="Payment Method" data-id="element-3184" />
              {showEditableForm ?
          // Editable payment method selection (new or future subscription)
          !customerId ? <div className="text-center py-6" data-id="element-3185">
                    <CreditCardIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-3186" />
                    <p className="text-sm text-gray-500" data-id="element-3187">
                      Select a customer to view payment methods
                    </p>
                  </div> : isLoadingPaymentMethods ? <div className="text-center py-6" data-id="element-3188">
                    <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2" data-id="element-3189"></div>
                    <p className="text-sm text-gray-500" data-id="element-3190">
                      Loading payment methods...
                    </p>
                  </div> : paymentMethods.length === 0 ? <div className="text-center py-6" data-id="element-3191">
                    <CreditCardIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-3192" />
                    <p className="text-sm text-gray-500" data-id="element-3193">
                      No saved payment methods
                    </p>
                  </div> : <div className="space-y-3" data-id="element-3194">
                    {paymentMethods.map(method => {
              const brand = getCardBrandDisplay(method.card_brand);
              const isSelected = selectedPaymentMethodId === method.id;
              return <div key={method.id} onClick={() => setSelectedPaymentMethodId(method.id)} className={`border rounded-lg p-4 transition-colors cursor-pointer ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3195">
                          <div className="flex items-center gap-3" data-id="element-3196">
                            <div className={`w-10 h-6 ${brand.color} rounded flex items-center justify-center text-white text-xs font-bold`} data-id="element-3197">
                              {brand.name.slice(0, 4)}
                            </div>
                            <div className="flex-1" data-id="element-3198">
                              <span className="text-sm text-gray-900" data-id="element-3199">
                                •••• {method.card_last_digits}
                              </span>
                              <p className="text-xs text-gray-500" data-id="element-3200">
                                Expires {method.card_exp}
                              </p>
                            </div>
                            {isSelected && <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-3201">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3202">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-3203" />
                                </svg>
                              </div>}
                          </div>
                        </div>;
            })}
                    {/* "Don't pay first invoice" option - for new non-future subscriptions OR future subscriptions with start date = today */}
                    {(!isEditing && !isStartDateFuture || isFutureSubscription && !isStartDateFuture) && <div onClick={() => setSelectedPaymentMethodId(SKIP_PAYMENT_ID)} className={`border rounded-lg p-4 transition-colors cursor-pointer ${selectedPaymentMethodId === SKIP_PAYMENT_ID ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3204">
                        <div className="flex items-center gap-3" data-id="element-3205">
                          <div className="w-10 h-6 bg-amber-500 rounded flex items-center justify-center" data-id="element-3206">
                            <XCircleIcon className="w-4 h-4 text-white" data-id="element-3207" />
                          </div>
                          <div className="flex-1" data-id="element-3208">
                            <span className="text-sm text-gray-900" data-id="element-3209">
                              Don't pay the first invoice right now
                            </span>
                            <p className="text-xs text-gray-500" data-id="element-3210">
                              Invoice will be created but not charged
                            </p>
                          </div>
                          {selectedPaymentMethodId === SKIP_PAYMENT_ID && <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-3211">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3212">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-3213" />
                              </svg>
                            </div>}
                        </div>
                      </div>}
                  </div> :
          // View mode - show only selected payment method
          selectedPaymentMethodId && paymentMethods.length > 0 ? (() => {
            const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethodId);
            if (!selectedMethod) {
              return <div className="text-center py-6" data-id="element-3214">
                        <CreditCardIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-3215" />
                        <p className="text-sm text-gray-500" data-id="element-3216">
                          No payment method on file
                        </p>
                      </div>;
            }
            const brand = getCardBrandDisplay(selectedMethod.card_brand);
            return <div className="border border-blue-500 bg-blue-50 rounded-lg p-4" data-id="element-3217">
                      <div className="flex items-center gap-3" data-id="element-3218">
                        <div className={`w-10 h-6 ${brand.color} rounded flex items-center justify-center text-white text-xs font-bold`} data-id="element-3219">
                          {brand.name.slice(0, 4)}
                        </div>
                        <div className="flex-1" data-id="element-3220">
                          <span className="text-sm text-gray-900" data-id="element-3221">
                            •••• {selectedMethod.card_last_digits}
                          </span>
                          <p className="text-xs text-gray-500" data-id="element-3222">
                            Expires {selectedMethod.card_exp}
                          </p>
                        </div>
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-3223">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3224">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-3225" />
                          </svg>
                        </div>
                      </div>
                    </div>;
          })() : <div className="text-center py-6" data-id="element-3226">
                  <CreditCardIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-3227" />
                  <p className="text-sm text-gray-500" data-id="element-3228">
                    No payment method on file
                  </p>
                </div>}
            </Card>}

          {/* Actions */}
          {showEditableForm && <Card data-id="element-3229">
              <Button className="w-full" onClick={handleSave} isLoading={isLoading} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3231" />} disabled={!customerId} data-id="element-3230">
                {isFutureSubscription ? 'Save Changes' : 'Create Subscription'}
              </Button>
              {!customerId && <p className="text-xs text-gray-500 text-center mt-2" data-id="element-3232">
                  Select a customer to{' '}
                  {isFutureSubscription ? 'save changes' : 'create subscription'}
                </p>}
            </Card>}
          {/* Cancel Subscription Button (for live subscriptions only) */}
          {canCancel && !isReadonly && <Card data-id="element-3233">
              <Button className="w-full" variant="danger" onClick={() => setShowCancelModal(true)} leftIcon={<XCircleIcon className="w-4 h-4" data-id="element-3235" />} data-id="element-3234">
                Cancel Subscription
              </Button>
            </Card>}
          {/* Delete Subscription Button (for future subscriptions only) */}
          {canDelete && !isReadonly && <Card data-id="element-3236">
              <Button className="w-full" variant="danger" onClick={() => setShowDeleteModal(true)} leftIcon={<Trash2Icon className="w-4 h-4" data-id="element-3238" />} data-id="element-3237">
                Delete Subscription
              </Button>
            </Card>}
        </div>
      </div>

      {/* Warning Modal */}
      <Modal isOpen={showWarningModal} onClose={handleWarningClose} title={isFutureSubscription ? 'Subscription Updated' : 'Subscription Created'} size="sm" data-id="element-3239">
        <div className="space-y-4" data-id="element-3240">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3" data-id="element-3241">
            <AlertCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5" data-id="element-3242" />
            <div data-id="element-3243">
              <p className="text-sm font-medium text-yellow-900" data-id="element-3244">Note</p>
              <p className="text-sm text-yellow-700 mt-1" data-id="element-3245">{warningMessage}</p>
            </div>
          </div>
          <div className="flex justify-end" data-id="element-3246">
            <Button onClick={handleWarningClose} data-id="element-3247">OK</Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Subscription Confirmation Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel Subscription" size="sm" data-id="element-3248">
        <div className="space-y-4" data-id="element-3249">
          <p className="text-gray-600" data-id="element-3250">
            Are you sure you want to cancel this subscription? This action
            cannot be undone.
          </p>
          {cancelError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-3251">
              <p className="text-sm text-red-600" data-id="element-3252">{cancelError}</p>
            </div>}
          <div className="flex justify-end gap-3" data-id="element-3253">
            <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={isCancelling} data-id="element-3254">
              Keep Subscription
            </Button>
            <Button variant="danger" onClick={handleCancelSubscription} isLoading={isCancelling} data-id="element-3255">
              Cancel Subscription
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Subscription Confirmation Modal (for future subscriptions) */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Subscription" size="sm" data-id="element-3256">
        <div className="space-y-4" data-id="element-3257">
          <p className="text-gray-600" data-id="element-3258">
            Are you sure you want to delete this subscription? This action
            cannot be undone.
          </p>
          {deleteError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-3259">
              <p className="text-sm text-red-600" data-id="element-3260">{deleteError}</p>
            </div>}
          <div className="flex justify-end gap-3" data-id="element-3261">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting} data-id="element-3262">
              Keep Subscription
            </Button>
            <Button variant="danger" onClick={handleDeleteSubscription} isLoading={isDeleting} data-id="element-3263">
              Delete Subscription
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}